import { useState, useEffect, useCallback } from 'react';
import api from '../../api';

/**
 * useSiteRequests
 *
 * For staff: fetches all requests (default status='pending').
 * Exposes approve / deny / remove actions.
 *
 * For users: use { mine: true } to fetch their own requests only.
 */
export default function useSiteRequests({ mine = false, status = 'pending' } = {}) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toastMessage, setToastMessage] = useState('');

  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRequests([]);
    try {
      const endpoint = mine ? '/site-requests/mine' : '/site-requests/';
      const params = status ? { status } : {};
      const res = await api.get(endpoint, { params });
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load site requests');
    } finally {
      setLoading(false);
    }
  }, [mine, status]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const submitRequest = useCallback(async (site) => {
    try {
      await api.post('/site-requests/', { site });
      showToast(`✓ Request submitted for "${site}"`);
      return { ok: true };
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to submit request';
      if (err?.response?.status === 409) {
        showToast(`ℹ ${detail}`);
        return { ok: false, detail };
      }
      showToast(`✗ ${detail}`);
      return { ok: false, detail };
    }
  }, [showToast]);

  const approveRequest = useCallback(async (id) => {
    try {
      await api.post(`/site-requests/${id}/approve`);
      showToast('✓ Site approved');
      fetchRequests();
    } catch (err) {
      showToast(`✗ ${err?.response?.data?.detail || 'Failed to approve'}`);
    }
  }, [fetchRequests, showToast]);

  const denyRequest = useCallback(async (id, reason = '') => {
    try {
      await api.post(`/site-requests/${id}/deny`, null, {
        params: reason ? { reason } : {},
      });
      showToast('Request denied');
      fetchRequests();
    } catch (err) {
      showToast(`✗ ${err?.response?.data?.detail || 'Failed to deny'}`);
    }
  }, [fetchRequests, showToast]);

  const deleteRequest = useCallback(async (id) => {
    try {
      await api.delete(`/site-requests/${id}`);
      showToast('Request deleted');
      fetchRequests();
    } catch (err) {
      showToast(`✗ ${err?.response?.data?.detail || 'Failed to delete'}`);
    }
  }, [fetchRequests, showToast]);

  return {
    requests,
    loading,
    error,
    toastMessage,
    refetch: fetchRequests,
    submitRequest,
    approveRequest,
    denyRequest,
    deleteRequest,
  };
}
